import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import BuildingBanner from '../components/BuildingBanner';
import { Megaphone, Plus, Trash2, AlertCircle, Info, Bell, Building2, Search, HelpCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Annonces = () => {
  const { token, user } = useAuth();
  const [annonces, setAnnonces] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedBuilding, setSelectedBuilding] = useState('');
  const [formData, setFormData] = useState({ titre: '', contenu: '', type: 'info', priority: 'normal', building_id: '' });

  useEffect(() => { 
    fetchAnnonces(); 
    fetchBuildings();
  }, [token]);

  const fetchBuildings = async () => {
    try {
      const response = await axios.get(`${API}/buildings`, { headers: { Authorization: `Bearer ${token}` } });
      setBuildings(response.data || []);
    } catch (error) {
      console.error('Error fetching buildings:', error);
    }
  };

  const fetchAnnonces = async () => {
    try {
      const response = await axios.get(`${API}/annonces`, { headers: { Authorization: `Bearer ${token}` } });
      setAnnonces(response.data);
    } catch (error) {
      console.error('Error fetching annonces:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/annonces`, formData, { headers: { Authorization: `Bearer ${token}` } });
      setShowModal(false);
      setFormData({ titre: '', contenu: '', type: 'info', priority: 'normal', building_id: '' });
      fetchAnnonces();
    } catch (error) {
      console.error('Error creating annonce:', error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette annonce ?')) {
      try {
        await axios.delete(`${API}/annonces/${id}`, { headers: { Authorization: `Bearer ${token}` } });
        fetchAnnonces();
      } catch (error) {
        console.error('Error deleting annonce:', error);
      }
    }
  };

  const getTypeIcon = (type) => {
    const icons = { info: Info, alerte: AlertCircle, notification: Bell };
    return icons[type] || Megaphone;
  };

  const getTypeColor = (type, priority) => {
    if (priority === 'urgent') return 'bg-red-50 border-red-200 text-red-700';
    const colors = { info: 'bg-blue-50 border-blue-200 text-blue-700', alerte: 'bg-orange-50 border-orange-200 text-orange-700', notification: 'bg-green-50 border-green-200 text-green-700' };
    return colors[type] || 'bg-gray-50 border-gray-200 text-gray-700';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const getBuildingName = (buildingId) => {
    const building = buildings.find(b => b.id === buildingId);
    return building?.nom || '';
  };

  const filteredAnnonces = annonces.filter(a => {
    if (!selectedBuilding) return true;
    return a.building_id === selectedBuilding;
  });

  const canManageAnnonces = ['gestionnaire', 'admin', 'superadmin', 'directeur_cabinet'].includes(user?.role);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00D3A8]"></div></div>;
  }

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="annonces-page">
      {/* Building Banner for Copropriétaires */}
      {user?.role === 'coproprietaire' && (
        <BuildingBanner token={token} user={user} compact={true} />
      )}
      
      {/* Header - Style unifié */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-blue-100 rounded-xl">
            <Megaphone className="h-8 w-8 text-blue-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Annonces</h1>
            <p className="text-gray-500">Publiez et consultez les annonces importantes</p>
          </div>
        </div>
        {canManageAnnonces && (
          <button onClick={() => setShowModal(true)}
            className="flex items-center gap-2 px-5 py-2.5 bg-[#00D3A8] text-white rounded-full font-medium shadow-md hover:bg-[#00B892] transition-all">
            <Plus size={18} /><span>Nouvelle annonce</span>
          </button>
        )}
      </div>

      {/* Filter by building */}
      <div className="mb-4">
        <div className="relative inline-block">
          <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
          <select 
            value={selectedBuilding} 
            onChange={(e) => setSelectedBuilding(e.target.value)}
            className="pl-10 pr-8 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#00D3A8]/20 focus:border-[#00D3A8] text-sm bg-white appearance-none min-w-[200px]"
            data-testid="building-filter"
          >
            <option value="">Tous les immeubles</option>
            {buildings.map(b => (
              <option key={b.id} value={b.id}>{b.nom}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="space-y-3 md:space-y-4">
        {filteredAnnonces.map((annonce) => {
          const Icon = getTypeIcon(annonce.type);
          return (
            <div key={annonce.id} className={`rounded-xl border p-4 md:p-5 ${getTypeColor(annonce.type, annonce.priority)}`}>
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div className="flex items-start space-x-3 md:space-x-4">
                  <div className="w-10 h-10 rounded-full bg-white/50 flex items-center justify-center flex-shrink-0">
                    <Icon size={20} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <h3 className="font-semibold text-sm md:text-base">{annonce.titre}</h3>
                      {annonce.priority === 'urgent' && (
                        <span className="text-xs bg-red-500 text-white px-2 py-0.5 rounded-full">URGENT</span>
                      )}
                    </div>
                    {annonce.building_id && getBuildingName(annonce.building_id) && (
                      <p className="text-xs font-medium mb-1 flex items-center gap-1 opacity-80">
                        <Building2 size={12} />
                        {getBuildingName(annonce.building_id)}
                      </p>
                    )}
                    <p className="text-xs md:text-sm opacity-80 mb-2">{annonce.contenu}</p>
                    <p className="text-xs opacity-60">{formatDate(annonce.created_at)}</p>
                  </div>
                </div>
                {canManageAnnonces && (
                  <button onClick={() => handleDelete(annonce.id)} className="p-1.5 opacity-60 hover:opacity-100 hover:bg-white/30 rounded-lg transition-colors self-start">
                    <Trash2 size={18} />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {filteredAnnonces.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 px-4 bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="w-20 h-20 bg-gradient-to-br from-slate-50 to-slate-100 rounded-full flex items-center justify-center mb-6 shadow-inner">
            <Megaphone className="text-slate-300" size={36} strokeWidth={1.5} />
          </div>
          <h3 className="text-xl font-semibold text-slate-700 mb-2 text-center">Aucune annonce publiée</h3>
          <p className="text-slate-500 text-sm max-w-lg mx-auto mb-4 text-center leading-relaxed">
            Les annonces permettent de communiquer des informations importantes à tous les coproprietaires : travaux, événements, rappels...
          </p>
          <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 mb-6 max-w-md">
            <div className="flex items-start gap-3">
              <div className="p-1.5 bg-amber-100 rounded-lg">
                <HelpCircle size={14} className="text-amber-600" />
              </div>
              <div>
                <p className="text-xs font-medium text-amber-800 mb-1">Conseil</p>
                <p className="text-xs text-amber-700">Les annonces urgentes sont mises en avant et envoient une notification aux coproprietaires.</p>
              </div>
            </div>
          </div>
          {canManageAnnonces && (
            <button
              onClick={() => setShowModal(true)}
              className="px-6 py-2.5 bg-[#00D3A8] text-white rounded-xl text-sm font-medium hover:bg-[#00B892] transition-all shadow-sm hover:shadow-md"
            >
              + Créer une annonce
            </button>
          )}
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl p-4 md:p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg md:text-xl font-semibold mb-4">Nouvelle annonce</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Immeuble concerné</label>
                <select value={formData.building_id} onChange={(e) => setFormData({...formData, building_id: e.target.value})}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00D3A8]/20 focus:border-[#00D3A8] text-sm"
                  data-testid="annonce-building-select">
                  <option value="">Tous les immeubles (général)</option>
                  {buildings.map(b => (
                    <option key={b.id} value={b.id}>{b.nom}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Titre</label>
                <input type="text" value={formData.titre} onChange={(e) => setFormData({...formData, titre: e.target.value})}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00D3A8]/20 focus:border-[#00D3A8] text-sm" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contenu</label>
                <textarea value={formData.contenu} onChange={(e) => setFormData({...formData, contenu: e.target.value})}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00D3A8]/20 focus:border-[#00D3A8] text-sm" rows={4} required />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                  <select value={formData.type} onChange={(e) => setFormData({...formData, type: e.target.value})}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00D3A8]/20 focus:border-[#00D3A8] text-sm">
                    <option value="info">Information</option>
                    <option value="alerte">Alerte</option>
                    <option value="notification">Notification</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Priorité</label>
                  <select value={formData.priority} onChange={(e) => setFormData({...formData, priority: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                    <option value="normal">Normale</option>
                    <option value="urgent">Urgente</option>
                  </select>
                </div>
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Annuler</button>
                <button type="submit" className="px-4 py-2 bg-[#00D3A8] text-white rounded-lg hover:bg-[#00C7A1]">Publier</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Annonces;
